import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDarkMode } from '../../contexts/DarkModeContext';
import { addLesson, getTeacherClasses, resetAddLessonState } from '../../redux/slices/lessonsSlice';
import { getTeacherSubjects } from '../../redux/slices/subjectsSlice';
import { toast } from 'react-toastify';
import './AddLessonModal.css';

function AddLessonModal({ isOpen, onClose, lessonType, onSuccess }) {
  const { isDarkMode } = useDarkMode();
  const dispatch = useDispatch();
  const { currentUser } = useSelector((state) => state.auth);
  const { addLessonLoading, addLessonError, addLessonSuccess, teacherClasses, teacherClassesLoading, teacherClassesError } = useSelector((state) => state.lessons);
  const { teacherSubjects, subjectsLoading } = useSelector((state) => state.subjects);

  const [formData, setFormData] = useState({
    title_ar: '',
    title_en: '',
    description_ar: '',
    description_en: '',
    class_id: '', // تغيير من subject_id إلى class_id
    subject_id: '', // إضافة subject_id مرة أخرى
    day: '',
    time: '',
    video_link: '',
    image: null,
    features: [{ title_ar: '', title_en: '' }],
    files: [{ file: null, title_ar: '', title_en: '' }]
  });

  const [previewImage, setPreviewImage] = useState(null);

  // جلب مواد المعلم عند فتح المودل
  useEffect(() => {
    if (isOpen) {
      dispatch(getTeacherClasses()); // Add this line to get teacher classes
      dispatch(getTeacherSubjects()); // إضافة جلب المواد مرة أخرى
      console.log('Modal opened, dispatching getTeacherClasses');
    }
  }, [isOpen, dispatch]);

  // مراقبة البيانات المحملة
  useEffect(() => {
    console.log('teacherClasses:', teacherClasses);
  }, [teacherClasses]);

  // مراقبة نجاح إضافة الحصة
  useEffect(() => {
    if (addLessonSuccess) {
      toast.success('تم إضافة الحصة الدراسية بنجاح');
      onSuccess();
      onClose();
      resetForm();
      // Reset Redux state after success
      dispatch(resetAddLessonState());
    }
  }, [addLessonSuccess, onSuccess, onClose, dispatch]);

  // مراقبة أخطاء إضافة الحصة
  useEffect(() => {
    if (addLessonError) {
      toast.error(addLessonError);
      // Reset Redux state after error
      dispatch(resetAddLessonState());
    }
  }, [addLessonError, dispatch]);

  // تحديد نوع الحصة حسب التاب النشط
  const getLessonTypeConfig = () => {
    switch (lessonType) {
      case 'الحصص المجانية':
        return { free_status: 1, video_type: 'offline' };
      case 'حصص اونلاين':
        return { free_status: 0, video_type: 'online' };
      case 'حصص مسجلة':
        return { free_status: 0, video_type: 'offline' };
      default:
        return { free_status: 0, video_type: 'offline' };
    }
  };

  // إنشاء رابط Jitsi للحصص الأونلاين
  const generateJitsiLink = () => {
    const roomName = `lesson_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    return `https://meet.jit.si/${roomName}`;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        image: file
      }));

      // إنشاء معاينة للصورة
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFileTitleChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      files: prev.files.map((file, i) =>
        i === index ? { ...file, [field]: value } : file
      )
    }));
  };

  const handleFeatureChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.map((feature, i) =>
        i === index ? { ...feature, [field]: value } : feature
      )
    }));
  };

  const addFeature = () => {
    setFormData(prev => ({
      ...prev,
      features: [...prev.features, { title_ar: '', title_en: '' }]
    }));
  };

  const removeFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const addFile = () => {
    setFormData(prev => ({
      ...prev,
      files: [...prev.files, { file: null, title_ar: '', title_en: '' }]
    }));
  };

  const removeFile = (index) => {
    setFormData(prev => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title_ar || !formData.description_ar || !formData.class_id || !formData.subject_id || !formData.day || !formData.time) {
      toast.error('يرجى ملء جميع الحقول المطلوبة');
      return;
    }

    // التحقق من أن كل ملف له عنوان واحد على الأقل
    const filesWithoutTitles = formData.files.filter(fileItem =>
      fileItem.file && (!fileItem.title_ar && !fileItem.title_en)
    );

    if (filesWithoutTitles.length > 0) {
      toast.error('يرجى إدخال عنوان واحد على الأقل (عربي أو إنجليزي) لكل ملف');
      return;
    }

    try {
      const lessonConfig = getLessonTypeConfig();
      const formDataToSend = new FormData();

      // إضافة البيانات الأساسية
      formDataToSend.append('title_ar', formData.title_ar);
      formDataToSend.append('title_en', formData.title_en);
      formDataToSend.append('description_ar', formData.description_ar);
      formDataToSend.append('description_en', formData.description_en);
      formDataToSend.append('class_id', formData.class_id);
      formDataToSend.append('subject_id', formData.subject_id);
      formDataToSend.append('day', formData.day);
      formDataToSend.append('time', formData.time);
      
      // إضافة free_status دائماً (مطلوب من الخادم)
      formDataToSend.append('free_status', lessonConfig.free_status);
      formDataToSend.append('video_type', lessonConfig.video_type);

      // إضافة رابط الفيديو
      if (lessonConfig.video_type === 'online') {
        // للحصص الأونلاين: إنشاء رابط Jitsi تلقائياً
        const jitsiLink = generateJitsiLink();
        formDataToSend.append('video_link', jitsiLink);
      } else {
        // للحصص المسجلة: إرسال الرابط المدخل يدوياً
        if (formData.video_link) {
          formDataToSend.append('video_link', formData.video_link);
        }
      }

      // إضافة الصورة
      if (formData.image) {
        formDataToSend.append('image', formData.image);
      }

      // إضافة المميزات
      formData.features.forEach((feature, index) => {
        if (feature.title_ar && feature.title_en) {
          formDataToSend.append(`features[${index}][title_ar]`, feature.title_ar);
          formDataToSend.append(`features[${index}][title_en]`, feature.title_en);
        }
      });

      // إضافة الملفات مع العناوين
      formData.files.forEach((fileItem, index) => {
        if (fileItem.file) {
          formDataToSend.append(`files[${index}][file]`, fileItem.file);
          formDataToSend.append(`files[${index}][title_ar]`, fileItem.title_ar || '');
          formDataToSend.append(`files[${index}][title_en]`, fileItem.title_en || '');
        }
      });

      console.log('formDataToSend - class_id:', formData.class_id);
      console.log('formDataToSend - subject_id:', formData.subject_id);
      console.log('formDataToSend - free_status:', lessonConfig.free_status);

      // إرسال البيانات باستخدام Redux
      dispatch(addLesson(formDataToSend));

    } catch (error) {
      console.error('Error preparing lesson data:', error);
      toast.error('حدث خطأ في إعداد بيانات الحصة');
    }
  };

  const resetForm = () => {
    setFormData({
      title_ar: '',
      title_en: '',
      description_ar: '',
      description_en: '',
      class_id: '',
      subject_id: '',
      day: '',
      time: '',
      video_link: '',
      image: null,
      features: [{ title_ar: '', title_en: '' }],
      files: [{ file: null, title_ar: '', title_en: '' }]
    });
    setPreviewImage(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  console.log('AddLessonModal render - isOpen:', isOpen);
  if (!isOpen) return null;

  return (
    <div className="addLessonModalNewContainer">
      <div className={`add-lesson-modal-overlay ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
        <div className="add-lesson-modal">
          <div className="modal-header">
          <h3>إضافة حصة دراسية جديدة</h3>
          <button className="close-btn" onClick={handleClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-section">
            <h4>معلومات الحصة الأساسية</h4>

            <div className="form-row">
              <div className="form-group">
                <label>عنوان الحصة (عربي) *</label>
                <input
                  type="text"
                  name="title_ar"
                  value={formData.title_ar}
                  onChange={handleInputChange}
                  placeholder="أدخل عنوان الحصة باللغة العربية"
                  required
                />
              </div>

              <div className="form-group">
                <label>عنوان الحصة (إنجليزي)</label>
                <input
                  type="text"
                  name="title_en"
                  value={formData.title_en}
                  onChange={handleInputChange}
                  placeholder="أدخل عنوان الحصة باللغة الإنجليزية"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>وصف الحصة (عربي) *</label>
                <textarea
                  name="description_ar"
                  value={formData.description_ar}
                  onChange={handleInputChange}
                  placeholder="أدخل وصف الحصة باللغة العربية"
                  rows="3"
                  required
                />
              </div>

              <div className="form-group">
                <label>وصف الحصة (إنجليزي)</label>
                <textarea
                  name="description_en"
                  value={formData.description_en}
                  onChange={handleInputChange}
                  placeholder="أدخل وصف الحصة باللغة الإنجليزية"
                  rows="3"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>الصف الدراسي *</label>
                <select
                  name="class_id"
                  value={formData.class_id}
                  onChange={handleInputChange}
                  required
                  disabled={teacherClassesLoading}
                >
                  <option value="">
                    {teacherClassesLoading ? 'جاري التحميل...' : 'اختر الصف الدراسي'}
                  </option>
                  {teacherClasses.map((classItem) => (
                    <option key={classItem.id} value={classItem.id}>
                      {classItem.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>المادة الدراسية *</label>
                <select
                  name="subject_id"
                  value={formData.subject_id}
                  onChange={handleInputChange}
                  required
                  disabled={subjectsLoading}
                >
                  <option value="">
                    {subjectsLoading ? 'جاري التحميل...' : 'اختر المادة الدراسية'}
                  </option>
                  {teacherSubjects.map((subject) => (
                    <option key={subject.id} value={subject.id}>
                      {subject.title}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>التاريخ *</label>
                <input
                  type="date"
                  name="day"
                  value={formData.day}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>الوقت *</label>
                <input
                  type="time"
                  name="time"
                  value={formData.time}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              {/* إظهار حقل رابط الفيديو للحصص المسجلة والمجانية */}
              {(lessonType === 'حصص مسجلة' || lessonType === 'الحصص المجانية') && (
                <div className="form-group">
                  <label>رابط الفيديو</label>
                  <input
                    type="url"
                    name="video_link"
                    value={formData.video_link}
                    onChange={handleInputChange}
                    placeholder="أدخل رابط الفيديو"
                  />
                </div>
              )}
              
              {/* لا تظهر أي شيء للحصص الأونلاين - الرابط يتم إنشاؤه تلقائياً */}
            </div>
          </div>

          <div className="form-section">
            <h4>صورة الحصة</h4>
            <div className="form-group">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="file-input"
              />
              {previewImage && (
                <div className="image-preview">
                  <img src={previewImage} alt="معاينة" />
                </div>
              )}
            </div>
          </div>

          <div className="form-section">
            <h4>مميزات الحصة</h4>
            {formData.features.map((feature, index) => (
              <div key={index} className="feature-row">
                <div className="form-group">
                  <label>العنوان (عربي)</label>
                  <input
                    type="text"
                    value={feature.title_ar}
                    onChange={(e) => handleFeatureChange(index, 'title_ar', e.target.value)}
                    placeholder="أدخل العنوان بالعربية"
                  />
                </div>

                <div className="form-group">
                  <label>العنوان (إنجليزي)</label>
                  <input
                    type="text"
                    value={feature.title_en}
                    onChange={(e) => handleFeatureChange(index, 'title_en', e.target.value)}
                    placeholder="أدخل العنوان بالإنجليزية"
                  />
                </div>
                

                {formData.features.length > 1 && (
                  <button
                    type="button"
                    className="remove-feature-btn"
                    onClick={() => removeFeature(index)}
                  >
                    حذف
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              className="add-feature-btn"
              onClick={addFeature}
            >
              إضافة ميزة جديدة
            </button>
          </div>

          <div className="form-section">
            <h4>ملفات الحصة</h4>
            {formData.files.map((fileItem, index) => (
              <div key={index} className="file-item">
                <div className="file-header">
                  {formData.files.length > 1 && (
                    <button
                      type="button"
                      className="remove-file-btn"
                      onClick={() => removeFile(index)}
                    >
                      حذف الملف
                    </button>
                  )}
                </div>

                <div className="file-input-section row">
                  <div className="form-group col-12 w-100">
                    <label>اختر الملف:</label>
                    <input
                      type="file"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          handleFileTitleChange(index, 'file', file);
                        }
                      }}
                      className="file-input"
                      accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx"
                    />
                  </div>
                  <div className="file-titles row">
                    <div className="form-group col-6">
                      <label>اسم الملف بالعربية:</label>
                      <input
                        type="text"
                        value={fileItem.title_ar}
                        onChange={(e) => handleFileTitleChange(index, 'title_ar', e.target.value)}
                        placeholder="أدخل اسم الملف بالعربية"
                        className="file-title-input"
                      />
                    </div>
                    <div className="form-group col-6">
                      <label>اسم الملف بالإنجليزية:</label>
                      <input
                        type="text"
                        value={fileItem.title_en}
                        onChange={(e) => handleFileTitleChange(index, 'title_en', e.target.value)}
                        placeholder="أدخل اسم الملف بالإنجليزية"
                        className="file-title-input"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}

            <button
              type="button"
              className="add-file-btn"
              onClick={addFile}
            >
              + إضافة ملف جديد
            </button>
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="cancel-btn"
              onClick={handleClose}
              disabled={addLessonLoading}
            >
              إلغاء
            </button>
            <button
              type="submit"
              className="submit-btn"
              disabled={addLessonLoading}
            >
              {addLessonLoading ? 'جاري الإضافة...' : 'إضافة الحصة'}
            </button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
}

export default AddLessonModal;